import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles, Heart, Shield, Truck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { getProducts, getCategories } from '../services/api';

const Home = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productsData, categoriesData] = await Promise.all([
          getProducts({ featured: true }),
          getCategories()
        ]);
        setProducts(productsData.slice(0, 6));
        setCategories(categoriesData);
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="loading-container">
        <p>Loading...</p>
      </div>
    );
  }

  const featuredProducts = products;

  return (
    <div className="home-page">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <h1 className="hero-title">
            Supporting Your<br />Spiritual Journey
          </h1>
          <p className="hero-subtitle">
            Discover handcrafted soaps, healing crystals, organic herbs, and spiritual essentials for your growth
          </p>
          <div className="hero-buttons">
            <Button asChild size="lg" className="hero-cta-primary">
              <Link to="/shop">
                Shop Now <ArrowRight className="ml-2" size={20} />
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="hero-cta-secondary">
              <Link to="/about">Learn Our Story</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="features-section">
        <div className="features-grid">
          <div className="feature-card">
            <div className="feature-icon">
              <Sparkles size={28} />
            </div>
            <h3 className="feature-title">Natural Ingredients</h3>
            <p className="feature-description">100% organic and ethically sourced</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon">
              <Heart size={28} />
            </div>
            <h3 className="feature-title">Handcrafted with Love</h3>
            <p className="feature-description">Made with care and intention</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon">
              <Shield size={28} />
            </div>
            <h3 className="feature-title">Quality Guaranteed</h3>
            <p className="feature-description">Premium quality you can trust</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon">
              <Truck size={28} />
            </div>
            <h3 className="feature-title">Free Shipping</h3>
            <p className="feature-description">On orders over $50</p>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="categories-section">
        <div className="section-header">
          <h2 className="section-title">Shop by Category</h2>
          <p className="section-subtitle">Explore our curated collection of natural wellness products</p>
        </div>
        <div className="categories-grid">
          {categories.map(category => (
            <Link to={`/shop?category=${category.id}`} key={category.id} className="category-card">
              <div className="category-image-wrapper">
                <img src={category.image} alt={category.name} className="category-image" />
                <div className="category-overlay"></div>
              </div>
              <div className="category-content">
                <h3 className="category-name">{category.name}</h3>
                <p className="category-description">{category.description}</p>
                <span className="category-link-text">
                  Explore <ArrowRight size={16} className="inline ml-1" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Featured Products */}
      <section className="featured-section">
        <div className="section-header">
          <h2 className="section-title">Featured Products</h2>
          <p className="section-subtitle">Our most loved natural wellness essentials</p>
        </div>
        <div className="products-grid">
          {featuredProducts.map(product => (
            <Card key={product.id} className="product-card">
              <Link to={`/product/${product.id}`}>
                <div className="product-image-wrapper">
                  <img src={product.image} alt={product.name} className="product-image" />
                </div>
                <div className="product-info">
                  <p className="product-category">{product.category}</p>
                  <h3 className="product-name">{product.name}</h3>
                  <p className="product-price">${product.price.toFixed(2)}</p>
                </div>
              </Link>
            </Card>
          ))}
        </div>
        <div className="text-center mt-12">
          <Button asChild size="lg">
            <Link to="/shop">View All Products <ArrowRight className="ml-2" size={18} /></Link>
          </Button>
        </div>
      </section>

      {/* About Preview */}
      <section className="about-preview-section">
        <div className="about-preview-grid">
          <div className="about-preview-image">
            <img src="https://images.unsplash.com/photo-1630057135574-6ac3a75fbdf7" alt="Our Story" />
          </div>
          <div className="about-preview-content">
            <h2 className="about-preview-title">Our Story</h2>
            <p className="about-preview-text">
              Let us cater to your spiritual essential needs. We strive to support your spiritual journey by supplying you items you may need to facilitate your spiritual growth.
            </p>
            <p className="about-preview-text">
              From handmade soaps to healing crystals, each item is chosen to support your spiritual journey. We're passionate about providing quality products that help you grow.
            </p>
            <Button asChild variant="outline" size="lg" className="mt-6">
              <Link to="/about">Read More About Us</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
